import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { Link } from "react-router-dom";
import powerDialerStore from "Stores/powerDialerStore";
import ToggleBtnType from "Components/common/ToggleBtnType";
import DropDownMenu from "Components/common/DropDownMenu";

const RING = [15, 20, 25, 30, 40, 60];
const COUNTDOWN = [
  [0, "Off"],
  [3, "3s"],
  [5, "5s"],
  [10, "10s"]
];
const OUTCOMES = ["No answer", "Busy", "Wrong number", "Left voicemail", "Disconnected"];
const ORDERS = [
  ["phone,mobilePhone,otherPhone", "Phone, Mobile, Other"],
  ["mobilePhone,phone,otherPhone", "Mobile, Phone, Other"],
  ["phone,otherPhone,mobilePhone", "Phone, Other, Mobile"]
];
const HOURS = Array.from({ length: 24 }, (_, h) => `${String(h).padStart(2, "0")}:00`);
const SKIP_HOURS = [0, 4, 12, 24, 48, 72, 168];
const NO_PHONE = [
  ["ask", "Ask me"],
  ["find", "Find while calling"],
  ["skip", "Skip"]
];

const hour12 = h => {
  const n = +h.split(":")[0];
  return `${n % 12 || 12}:00 ${n < 12 ? "am" : "pm"}`;
};

const Row = ({ label, desc, children }) => (
  <div className="pdRow">
    <div>
      {label}
      {desc && <div className="pdDesc">{desc}</div>}
    </div>
    {children}
  </div>
);

const Select = ({ value, options, onChange }) => {
  const [open, setOpen] = useState(false);
  const current = options.find(([v]) => String(v) === String(value));
  const menu = options.map(([v, label]) => ({
    key: String(v),
    value: label,
    method: () => {
      setOpen(false);
      onChange(String(v));
    }
  }));
  return (
    <div className="pdSelect" onKeyDown={e => e.key === "Escape" && setOpen(false)}>
      <button type="button" className="pdSelectValue" aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen(!open)}>
        <span>{current ? current[1] : ""}</span>
        <i className="material-icons-outlined">{open ? "expand_less" : "expand_more"}</i>
      </button>
      {open && (
        <>
          <DropDownMenu options={menu} selected={String(value)} direction="center" />
          <div className="dropdownOverlay" onClick={() => setOpen(false)} />
        </>
      )}
    </div>
  );
};

// Radio group: Tab reaches the selected option, arrow keys move and select (WAI-ARIA radio pattern).
const Segmented = ({ value, options, onChange, label }) => {
  const index = Math.max(0, options.findIndex(([v]) => String(v) === String(value)));
  const onKeyDown = e => {
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
    if (!step) return;
    e.preventDefault();
    const next = (index + step + options.length) % options.length;
    onChange(options[next][0]);
    const target = e.currentTarget.querySelectorAll("button")[next];
    if (target) target.focus();
  };
  return (
    <div className="pdSeg" role="radiogroup" aria-label={label} onKeyDown={onKeyDown}>
      {options.map(([v, text], i) => (
        <button
          type="button"
          key={v}
          role="radio"
          aria-checked={i === index}
          tabIndex={i === index ? 0 : -1}
          className={i === index ? "on" : ""}
          onClick={() => onChange(v)}
        >
          {text}
        </button>
      ))}
    </div>
  );
};

const PowerDialer = observer(() => {
  const { settings: s, saveSettings, loadSettings } = powerDialerStore;

  useEffect(() => {
    loadSettings();
  }, []);

  const toggle = (key, label, desc) => (
    <Row label={label} desc={desc}>
      <ToggleBtnType id={key} defaultValue={!!s[key]} cbk={v => saveSettings({ [key]: v })} />
    </Row>
  );
  const mapping = s.autoLogMapping || {};
  const setMapping = (key, v) => saveSettings({ autoLogMapping: { ...mapping, [key]: v } });
  const hours = s.callingHours || {};

  return (
    <div className="pdSettings">
      <div className="pdCard">
        <h3>Pacing</h3>
        <Row label="Ring each number for" desc="Then it logs No answer and moves on">
          <Select
            value={s.ringSeconds}
            options={RING.map(v => [v, `${v} seconds`])}
            onChange={v => saveSettings({ ringSeconds: +v })}
          />
        </Row>
        <Row label="Countdown before the next call">
          <Segmented
            label="Countdown before the next call"
            value={s.countdownSeconds}
            options={COUNTDOWN}
            onChange={v => saveSettings({ countdownSeconds: v })}
          />
        </Row>
        <Row label="After a connected call">
          <div className="pdRadios">
            <label>
              <input
                type="radio"
                checked={s.afterConnected !== "autolog"}
                onChange={() => saveSettings({ afterConnected: "wait" })}
              />
              Wait until I log the outcome
            </label>
            <label>
              <input
                type="radio"
                checked={s.afterConnected === "autolog"}
                onChange={() => saveSettings({ afterConnected: "autolog" })}
              />
              Auto-log connected calls when the AI is confident
            </label>
            <div className="pdDesc">
              Logs after a 5s countdown with Change and Log now. The outcome is always pre-picked from the call; low
              confidence still asks you.
            </div>
          </div>
        </Row>
      </div>

      <div className="pdCard">
        <h3>Auto-logging</h3>
        {[
          ["no-answer", "No answer"],
          ["busy", "Busy"],
          ["failed", "Failed or invalid number"]
        ].map(([key, label]) => (
          <Row key={key} label={label}>
            <Select value={mapping[key]} options={OUTCOMES.map(o => [o, o])} onChange={v => setMapping(key, v)} />
          </Row>
        ))}
        {toggle("markNumberBad", "Mark the number bad after Wrong number", "Next time it tries the contact’s other number")}
      </div>

      <div className="pdCard">
        <h3>Who gets called</h3>
        <Row label="Number order">
          <Select
            value={(s.numberOrder || []).join(",")}
            options={ORDERS}
            onChange={v => saveSettings({ numberOrder: v.split(",") })}
          />
        </Row>
        {toggle("tryNextNumberOnNoAnswer", "No answer on the first number", "Try the next number right away")}
        <Row label="Calling hours (contact’s local time)">
          <div className="pdHours">
            <Select
              value={hours.from}
              options={HOURS.map(h => [h, hour12(h)])}
              onChange={v => saveSettings({ callingHours: { ...hours, from: v } })}
            />
            <span>to</span>
            <Select
              value={hours.to}
              options={HOURS.map(h => [h, hour12(h)])}
              onChange={v => saveSettings({ callingHours: { ...hours, to: v } })}
            />
          </div>
        </Row>
        <Row label="Skip if called in the last">
          <Select
            value={s.skipCalledWithinHours}
            options={SKIP_HOURS.map(h => [h, h ? `${h} hours` : "Don’t skip"])}
            onChange={v => saveSettings({ skipCalledWithinHours: +v })}
          />
        </Row>
        {toggle("skipRepliedOrOptedOut", "Skip contacts who replied or opted out")}
        {toggle("redialNoAnswersAtEnd", "Call no-answers again at the end")}
      </div>

      <div className="pdCard">
        <h3>Advanced</h3>
        <Row
          label="Contacts without a phone number"
          desc="Find while calling looks numbers up one at a time, ahead of their turn. Uses enrichment credits."
        >
          <Segmented
            label="Contacts without a phone number"
            value={s.noPhoneNumber}
            options={NO_PHONE}
            onChange={v => saveSettings({ noPhoneNumber: v })}
          />
        </Row>
        <Row label="If no number is found" desc="Skipped. The task stays due and shows in the session summary." />
      </div>

      <div className="pdCard">
        <h3>Voicemail</h3>
        <Row
          label="Voicemail drop works the same in click-to-call and the power dialer"
          desc={`Auto drop is ${s.autoVoicemailDrop ? "on" : "off"}`}
        >
          <Link className="pdLink" to="/settings/dialer/voicemail">
            Voicemail settings
          </Link>
        </Row>
      </div>
    </div>
  );
});

export default PowerDialer;
